import React from 'react';
import {useTranslation} from 'react-i18next';
import {Plugs, Prohibit, Question, UserPlus, Users, WarningCircle} from '../icons';

/**
 * El vacío de la sección de uso. NO es un "no hay datos" gris: en dos de los cuatro casos el vacío
 * ES la funcionalidad.
 *
 * - `INVITED_UNUSED` — el cliente tiene acceso y nunca lo usó. Es el estado más valioso de todos y
 *   hoy es invisible: la agencia ya hizo el trabajo de configurar el portal y no está cobrando el
 *   beneficio. Por eso lleva los nombres de quiénes tienen acceso, que es con quién hay que hablar.
 * - `NO_USERS` — nadie invitado todavía. Ahí sí hay una acción in-product: invitar.
 * - `MODULE_OFF` — el asistente está apagado para este cliente; no hay nada que medir.
 * - `ERROR` — no se pudo calcular. Se ofrece reintentar, nunca un cero (un cero acá se leería como
 *   "tu cliente no usa el portal", que es una afirmación distinta de "no pudimos preguntarlo").
 */
export function PortalUsageEmptyState({kind, portalUsers, onInvite, onRetry, count}) {
    const {t} = useTranslation();

    if (kind === 'INVITED_UNUSED') {
        const names = (portalUsers || []).map(user => user.name || user.email).filter(Boolean);
        return (
            <div className="pu-empty -ia">
                <span className="pu-empty__ico"><UserPlus size={19}/></span>
                <h4>{t('portal_usage.empty_invited_title', 'Con acceso, todavía sin actividad')}</h4>
                {/* Singular y plural como dos keys: con dos nombres, "tiene acceso" es un error
                    de concordancia que se lee en la primera línea de la pantalla. */}
                <p>
                    {names.length === 0 && t('portal_usage.empty_invited_plain',
                        'Este cliente tiene acceso al portal y todavía no entró ni hizo consultas.')}
                    {names.length === 1 && t('portal_usage.empty_invited_named',
                        '{{names}} tiene acceso al portal y todavía no entró ni hizo consultas. Un recordatorio suele alcanzar.',
                        {names: names[0], interpolation: {escapeValue: false}})}
                    {names.length > 1 && t('portal_usage.empty_invited_named_many',
                        '{{names}} tienen acceso al portal y todavía no entraron ni hicieron consultas. Un recordatorio suele alcanzar.',
                        {names: names.join(', '), interpolation: {escapeValue: false}})}
                </p>
            </div>
        );
    }

    if (kind === 'NO_USERS') {
        return (
            <div className="pu-empty -neutral">
                <span className="pu-empty__ico"><Users size={19}/></span>
                <h4>{t('portal_usage.empty_no_users_title', 'Todavía no invitaste a nadie')}</h4>
                <p>
                    {t('portal_usage.empty_no_users_text',
                        'Cuando tu cliente entre al portal y le pregunte a su IA, vas a ver acá qué usa y qué pregunta.')}
                </p>
                {onInvite ? (
                    <div className="pu-empty__cta">
                        <button className="btn -alt" onClick={onInvite}>
                            {t('portal_usage.empty_no_users_cta', 'Invitar al portal')}
                        </button>
                    </div>
                ) : null}
            </div>
        );
    }

    if (kind === 'CONTEXT_ONLY') {
        // El asistente se conectó y trabajó, pero el cliente nunca llegó a pedir un número. Suele
        // ser una conexión a medio configurar, y es accionable: hay a quién preguntarle.
        return (
            <div className="pu-empty -warning">
                <span className="pu-empty__ico"><Plugs size={19}/></span>
                <h4>
                    {t('portal_usage.empty_context_only_title',
                        'Su asistente se conectó, pero todavía no consultó datos')}
                </h4>
                <p>
                    {t('portal_usage.empty_context_only_text',
                        'El asistente de tu cliente leyó el contexto {{n}} veces y nunca llegó a pedir '
                        + 'un número. Suele pasar cuando la conexión quedó a medio configurar: vale la '
                        + 'pena preguntarle si le funciona.', {n: count || 0})}
                </p>
            </div>
        );
    }

    if (kind === 'NO_QUESTIONS') {
        return (
            <div className="pu-empty -neutral">
                <span className="pu-empty__ico"><Question size={19}/></span>
                <h4>{t('portal_usage.empty_no_questions_title', 'Todavía no hay ninguna consulta')}</h4>
                <p>
                    {t('portal_usage.empty_no_questions_text',
                        'Las {{n}} interacciones de este período son pasos que hace el asistente para '
                        + 'juntar contexto. Cuando tu cliente le pida un dato concreto, vas a verlo acá.',
                        {n: count || 0})}
                </p>
            </div>
        );
    }

    if (kind === 'MODULE_OFF') {
        return (
            <div className="pu-empty -neutral">
                <span className="pu-empty__ico"><Prohibit size={19}/></span>
                <h4>{t('portal_usage.empty_module_off_title', 'El asistente está apagado')}</h4>
                <p>
                    {t('portal_usage.empty_module_off_text',
                        'Este cliente no tiene habilitado el asistente en su portal. Se prende arriba, en los módulos del portal.')}
                </p>
            </div>
        );
    }

    return (
        <div className="pu-empty -warning">
            <span className="pu-empty__ico"><WarningCircle size={19}/></span>
            <h4>{t('portal_usage.empty_error_title', 'No pudimos calcular el uso')}</h4>
            <p>
                {t('portal_usage.empty_error_text',
                    'Vuelve a intentarlo en un momento. Los datos se siguen registrando igual.')}
            </p>
            {onRetry ? (
                <div className="pu-empty__cta">
                    <button className="btn -alt -negative" onClick={onRetry}>
                        {t('portal_usage.empty_error_cta', 'Reintentar')}
                    </button>
                </div>
            ) : null}
        </div>
    );
}

export default PortalUsageEmptyState;
